import React from 'react'
import styled from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { useIntl } from 'gatsby-plugin-intl'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { MARKETPLACEOFFER_TRACKING_EVENT_TYPES } from 'src/ducks/consts'
import useOfferTracking from 'src/features/marketplace/hooks/useMarketplaceOfferTracking'
import getOfferWebsite from 'src/features/marketplace/utils/getOfferWebsite'

const OfferCard = ({ offer, onClick }) => {
  const intl = useIntl()
  const { trackEvent } = useOfferTracking()
  const title = `${offer.isDraft ? '-DRAFT- ' : ''}${offer.title || ''}`
  const website = getOfferWebsite(offer.landingPageUrl)
  const websiteLabel = website
    ? intl.formatMessage(
        {
          id: 'marketplace.companyWebsiteLabel',
          defaultMessage: 'Company website: {domain} (opens in a new tab)'
        },
        { domain: website.domain }
      )
    : undefined
  const imageSource = offer.cardImage?.url || offer.logo?.url
  const isLogoOnly = !offer.cardImage?.url

  const track = type => {
    // Analytics must never delay navigation or prevent opening an offer.
    trackEvent(offer.id, type).catch(() => {})
  }

  const handleDetailsClick = () => {
    onClick(offer)
    track(MARKETPLACEOFFER_TRACKING_EVENT_TYPES.OFFER_VIEWED)
  }

  const handleWebsiteClick = () => {
    track(MARKETPLACEOFFER_TRACKING_EVENT_TYPES.OFFER_CLICKED)
  }

  return (
    <CardWrapper>
      <ImageContainer isLogoOnly={isLogoOnly}>
        {imageSource && (
          <OfferImage
            src={imageSource}
            alt=''
            isLogoOnly={isLogoOnly}
            onError={event => {
              const image = event.currentTarget
              image.style.display = 'none'
            }}
          />
        )}
      </ImageContainer>
      <DetailsButton
        type='button'
        onClick={handleDetailsClick}
        aria-label={intl.formatMessage(
          {
            id: 'marketplace.offerDetailsLabel',
            defaultMessage: 'Offer details: {title}'
          },
          { title }
        )}
      />
      <CardContent>
        {(offer.offerHook || website) && (
          <OfferHighlights>
            {offer.offerHook && <DiscountTag>{offer.offerHook}</DiscountTag>}
            {website && (
              <WebsiteLink
                href={website.href}
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleWebsiteClick}
                onAuxClick={event => {
                  if (event.button === 1) handleWebsiteClick()
                }}
                aria-label={websiteLabel}
                title={websiteLabel}
              >
                <OpenInNewIcon aria-hidden='true' />
              </WebsiteLink>
            )}
          </OfferHighlights>
        )}
        <CardTitle role='heading' aria-level={3}>
          <ReactMarkdown>{title}</ReactMarkdown>
        </CardTitle>
        <CardDescription>
          <ReactMarkdown>{offer.description || ''}</ReactMarkdown>
        </CardDescription>
      </CardContent>
    </CardWrapper>
  )
}

export default OfferCard

const CardWrapper = styled.article`
  &,
  & * {
    box-sizing: border-box;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: ${({ theme }) => theme.colors.white.hex()};
  border-radius: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  position: relative;
  padding-top: 40%;
  overflow: hidden;
  background: ${({ theme, isLogoOnly }) =>
    isLogoOnly ? theme.colors.white.hex() : theme.colors.wildSand.hex()};
`

const OfferImage = styled.img`
  position: absolute;
  top: ${({ isLogoOnly }) => (isLogoOnly ? '7.5%' : '0')};
  left: ${({ isLogoOnly }) => (isLogoOnly ? '7.5%' : '0')};
  width: ${({ isLogoOnly }) => (isLogoOnly ? '85%' : '100%')};
  height: ${({ isLogoOnly }) => (isLogoOnly ? '85%' : '100%')};
  object-fit: ${({ isLogoOnly }) => (isLogoOnly ? 'contain' : 'cover')};
  display: block;
`

const CardContent = styled.div`
  flex: 1;
  position: relative;
  padding: 1.5rem;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 100px;
  overflow-wrap: anywhere;

  p {
    margin: 0;
  }

  a {
    position: relative;
    z-index: 2;
  }
`

const CardTitle = styled.div`
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black.hex()};
`

const CardDescription = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const OfferHighlights = styled.div`
  --offer-action-size: 1.875rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin: calc(-1.5rem - var(--offer-action-size) / 2) calc(10px - 1.5rem)
    0.5rem 0;
`

const DiscountTag = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: var(--offer-action-size);
  background: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  padding: 0.3rem 0.6rem;
  font-weight: bold;
  font-size: 0.9rem;
  line-height: 1.4;
`

const DetailsButton = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  cursor: pointer;

  /* The separate website link sits above this native card button. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ebonyClay.hex()};
    outline-offset: 3px;
  }
`

const WebsiteLink = styled.a`
  position: relative;
  z-index: 2;
  flex: 0 0 var(--offer-action-size);
  width: var(--offer-action-size);
  height: var(--offer-action-size);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: ${({ theme }) => theme.colors.apple.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  text-decoration: none;
  transition: background-color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.white.hex()};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ebonyClay.hex()};
    outline-offset: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.apple.darken(0.4).hex()};
      text-decoration: none;
    }
  }

  &:active {
    background: ${({ theme }) => theme.colors.apple.darken(0.7).hex()};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
